import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Container, Typography } from '@material-ui/core';
import { Col } from 'components/Col';
import { Row } from 'components/Row';
import { CustomCheckbox } from 'components/CustomCheckbox';
import useValidations from '../hooks/useValidations';
import CustomMultipleSelect from 'components/CustomMultipleSelect/CustomMultipleSelect';
import { CustomTextField } from 'components/CustomTextField';
import { DateMaskedInput } from 'components/MaskedInputs';
import { diferenceMonths } from 'utils/DateUtils';
import { allProgramas } from 'models/escola';
import { RootState } from 'store';
import { changeContratoEntePublicoEditingValue } from 'store/modules/ContratosFluxoDescentralizado/EntePublico/EntePublicoActions';
import { NewAutoCompleteField } from 'components/NewAutoCompleteField';
import SnackbarUtils from 'utils/SnackbarUtils';
import { loadCidadesByEstadoId, loadEstados } from 'store/modules/LogradouroActions';
import '../index.scss';
import { AlturaMenuEnum } from 'components/CustomMultipleSelect/__types';

interface Cidade {
  nome: string;
}

export const ContratoEntePublicoDescentralizado = () => {
  const [dataInicioError, setDataInicioError] = useState('');
  const [dataFimError, setDataFimError] = useState('');
  const [, setIdEstadoChanged] = useState(false);
  const languageValues = useSelector((state: RootState) => state.languages.languageValues);
  const logradouro = useSelector((state: RootState) => state.logradouro);
  const contratosEntePublicoReducer = useSelector((state: RootState) => state.contratosDescentralizadosEntePublico);
  const dispatch = useDispatch();

  const {
    isContratoAtivoEntePublico,
    dataInicioIsValid,
    dataFimIsValid,
    validateDataInicio,
    validateDataFim,
    validaStatusContratoFluxo,
    valueProgramaEducacionaisIsValid,
    validaValorNomeEstado,
    validaValorNomeCidade,
    desabilitaCampoCidade
  } = useValidations();

  const handleDataChange = useCallback((name, value) => {
    dispatch(changeContratoEntePublicoEditingValue(name, value));
  }, [dispatch])

  const getUFByIdEstado = (idEstadoParam: number | null): string => {
    const estadoV = logradouro.estados.find((estado) => estado.id === idEstadoParam);
    return estadoV ? estadoV.sigla : '';
  };

  const loadEstadosAll = () => {
    dispatch(loadEstados(
      () => {
        if (contratosEntePublicoReducer.contratoEditing.idEstado) {
          loadCidades(contratosEntePublicoReducer.contratoEditing.idEstado);
        }
      },
      (error: any) => {
        SnackbarUtils.enqueueSnackbar({
          message: error.response.statusText || languageValues.errorLoadEstados,
          options: {
            variant: 'error',
          },
        });
      }
    ));
  };

  const getCidades = (uf: string): Cidade[] => {
    if (!uf) {
      return [];
    }
    return logradouro.cidades[uf.toLowerCase()] || [];
  };

  const loadCidades = (idEstado: number) => {
    const uf = getUFByIdEstado(idEstado);
    if (uf && getCidades(uf).length === 0) {
      dispatch(loadCidadesByEstadoId(idEstado, uf));
    }
  };

  const handleEstadoChange = (value: any) => {
    const currValue = value && value.value > -1 ? value.value : -1;
    setIdEstadoChanged(true);
    handleDataChange('idEstado', currValue);
    handleDataChange('nomCidade', '');
    loadCidades(currValue);
  };

  const onDataInicioChanged = (value: string) => {
    const error = validateDataInicio(
      value,
      contratosEntePublicoReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    setDataInicioError(error);
    if (!error) {
      setDataInicioError('');
    }

    let months: unknown = diferenceMonths(
      value,
      contratosEntePublicoReducer.contratoEditing.dataFim
    );
    if (typeof months === 'string') {
      months = parseFloat(months);
    }
    if (isNaN(months as number)) {
      months = 0;
    }
    handleDataChange('totalMeses', Math.max(months as number, 0));
  };

  const onDataFimChanged = (value: string) => {
    const error = validateDataFim(
      value,
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    setDataFimError(error);
    if (!error) {
      setDataFimError('');
    }
    const dataInicioErrorValue = validateDataInicio(
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      value,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    setDataInicioError(dataInicioErrorValue);

    let months: unknown = diferenceMonths(
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      value
    );
    if (typeof months === 'string') {
      months = parseFloat(months);
    }
    if (isNaN(months as number)) {
      months = 0;
    }
    handleDataChange('totalMeses', Math.max(months as number, 0));
  };

  const dataInicioValidacaoResultado = dataInicioIsValid(
    contratosEntePublicoReducer.contratoEditing.dataInicio,
    contratosEntePublicoReducer.contratoEditing.dataFim,
    {
      after: languageValues.dataInicioError,
      same: languageValues.dataInicioIgualDataFimError,
    }
  );

  const dataFimValidacaoResultado = dataFimIsValid(
    contratosEntePublicoReducer.contratoEditing.dataFim,
    contratosEntePublicoReducer.contratoEditing.dataInicio,
    {
      before: languageValues.dataFimError,
      same: languageValues.dataFimIgualDataInicioError,
    }
  );

  useEffect(() => {
    loadEstadosAll();
    // eslint-disable-next-line 
  }, []);

  return (
    <Container className='componentsDescentralizado__container'>
      <Box className='componentsDescentralizado__box'>
        <Typography variant='h5' className='componentsDescentralizado__title'>
          {languageValues.dadosContratante}
        </Typography>
        <Typography variant='h5' className='componentsDescentralizado__sub'>
          {languageValues.preenchaDadosContratanteEntePublico}
        </Typography>
      </Box>

      <Card className='componentsDescentralizado__card'>
        <Box>
          <Row>
            <Col cols={[12, 12, 12, 12, 12]}>
              <Typography variant='h5' id="dadosEntePublico"
                className='componentsDescentralizado__tipo-contrato-title'>
                {languageValues.cadastroContratoDadosEntePublico}
              </Typography>
            </Col>
          </Row>
          <Row>
            <Col cols={[12, 6, 6, 6, 6]}>
              <NewAutoCompleteField
                id="estado"
                label={languageValues.estado}
                placeholder={
                  logradouro.loadingAllEstadosByCoops
                    ? languageValues.loadingEstados
                    : ''
                }
                options={logradouro.estados.map((item) => ({
                  label: item.nome,
                  value: item.id,
                  sigla: item.sigla,
                }))}
                value={contratosEntePublicoReducer.contratoEditing.idEstado}
                onChange={handleEstadoChange}
                loading={logradouro.loadingAllEstadosByCoops}
                loadingMessage={languageValues.loadingEstados}
                error={!validaValorNomeEstado(contratosEntePublicoReducer.contratoEditing.idEstado).status}
                helperText={
                  !validaValorNomeEstado(contratosEntePublicoReducer.contratoEditing.idEstado).status
                    ? validaValorNomeEstado(contratosEntePublicoReducer.contratoEditing.idEstado).error
                    : ''
                }
                disabled={
                  !isContratoAtivoEntePublico() ||
                  !validaStatusContratoFluxo()
                }
              />
            </Col>
            <Col cols={[12, 6, 6, 6, 6]}>
              <NewAutoCompleteField
                id="cidade"
                label={languageValues.cidade}
                placeholder={
                  logradouro.loadingCidades
                    ? languageValues.loadingCidades
                    : ''
                }
                options={getCidades(
                  getUFByIdEstado(
                    contratosEntePublicoReducer.contratoEditing.idEstado
                  )
                ).map((item) => ({
                  label: item.nome,
                  value: item.nome,
                }))}
                value={contratosEntePublicoReducer.contratoEditing.nomCidade}
                onChange={(value: { value: string }) => {
                  const currValue = value ? value.value : '';
                  setIdEstadoChanged(true);
                  handleDataChange('nomCidade', currValue);
                }}
                loading={logradouro.loadingCidades}
                loadingMessage={languageValues.loadingCidades}
                helperText={
                  !desabilitaCampoCidade(contratosEntePublicoReducer.contratoEditing.idEstado < 1) &&
                    !validaValorNomeCidade(contratosEntePublicoReducer.contratoEditing.nomCidade).status
                    ? validaValorNomeCidade(contratosEntePublicoReducer.contratoEditing.nomCidade).error
                    : ''
                }
                error={
                  !desabilitaCampoCidade(contratosEntePublicoReducer.contratoEditing.idEstado < 1) &&
                  !validaValorNomeCidade(contratosEntePublicoReducer.contratoEditing.nomCidade).status
                }
                disabled={
                  !isContratoAtivoEntePublico() ||
                  !validaStatusContratoFluxo() ||
                  desabilitaCampoCidade(contratosEntePublicoReducer.contratoEditing.idEstado < 1)
                }
              />
            </Col>
          </Row>

          <Row>
            <Col cols={[12, 12, 12, 12, 12]}>
              <Typography variant='h5' id="dadosEntePublico" className='componentsDescentralizado__tipo-contrato-title'>
                {languageValues.cadastroContratoDadosContrato}
              </Typography>
            </Col>
          </Row>
          <Row>
            <Col cols={[12, 12, 12, 12, 12]}>
              <CustomMultipleSelect
                id={'programas-educacionais'}
                label={languageValues.programasEducacionais}
                options={allProgramas() && allProgramas().map((item) => ({
                  label: item.description,
                  value: item.value,
                }))}
                multiple
                value={contratosEntePublicoReducer.contratoEditing.programasEducacionais}
                onChange={(value) => {
                  handleDataChange('programasEducacionais', value);
                }}
                error={!valueProgramaEducacionaisIsValid().status}
                helperText={
                  !valueProgramaEducacionaisIsValid().status
                    ? valueProgramaEducacionaisIsValid().error
                    : ''}
                disabled={
                  !isContratoAtivoEntePublico() ||
                  !validaStatusContratoFluxo()}
                menuMaxHeight={{
                  desktop: AlturaMenuEnum.full,
                  tablet: AlturaMenuEnum.full,
                  mobile: AlturaMenuEnum.full
                }}
              />
            </Col>
          </Row>

          <Row>
            <Col cols={[12, 12, 6, 6, 6]}>
              <CustomTextField
                id={'data-inicio'}
                label={languageValues.cadastroContratoDataInicio}
                name='dataInicio'
                value={contratosEntePublicoReducer.contratoEditing.dataInicio}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value.trim();
                  handleDataChange('dataInicio', value);
                  if (value.length === 10) {
                    onDataInicioChanged(value);
                  } else {
                    setDataInicioError('');
                  }
                }}
                InputProps={{
                  inputComponent: DateMaskedInput,
                }}
                helperText={
                  dataInicioError ||
                  (!dataInicioValidacaoResultado.status ? dataInicioValidacaoResultado.error : '')
                }
                error={!dataInicioValidacaoResultado.status || !!dataInicioError}
                disabled={
                  !isContratoAtivoEntePublico() ||
                  !validaStatusContratoFluxo()}
              />
            </Col>
            <Col cols={[12, 12, 6, 6, 6]}>
              <CustomTextField
                id={'data-fim'}
                label={languageValues.cadastroContratoDataFim}
                name='dataFim'
                value={contratosEntePublicoReducer.contratoEditing.dataFim}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value.trim();
                  handleDataChange('dataFim', value);
                  if (value.length === 10) {
                    onDataFimChanged(value);
                  } else {
                    setDataFimError('');
                  }
                }}
                InputProps={{
                  inputComponent: DateMaskedInput,
                }}
                helperText={
                  dataFimError ||
                  (!dataFimValidacaoResultado.status ? dataFimValidacaoResultado.error : '')
                }
                error={!dataFimValidacaoResultado.status || !!dataFimError}
                disabled={
                  !isContratoAtivoEntePublico() ||
                  !validaStatusContratoFluxo()} />
            </Col>
          </Row>

          <Row className="componentsDescentralizado__row-content">
            <Col cols={[12, 12, 6, 6, 6]}>
              <div className='componentsDescentralizado__label-total-meses'>
                {languageValues.totalMesesContratados}
              </div>
              <div className='componentsDescentralizado__value-total-meses'>
                {contratosEntePublicoReducer.contratoEditing.totalMeses
                  ? contratosEntePublicoReducer.contratoEditing.totalMeses.toString()
                  : '0'}
              </div>
            </Col>
          </Row>

          <Row>
            <Col cols={[12, 12, 6, 6, 6]}>
              <CustomCheckbox
                id={'analise-juridica'}
                className={'componentsDescentralizado__analise-juridica'}
                label={languageValues.flgAnaliseJuridicaAceite}
                name='flgAnaliseJuridica'
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleDataChange('flgAnaliseJuridica', event.target.checked);
                }}
                checked={contratosEntePublicoReducer.contratoEditing.flgAnaliseJuridica}
                disabled={
                  !isContratoAtivoEntePublico() ||
                  !validaStatusContratoFluxo()}
              />
            </Col>
          </Row>
        </Box>
      </Card>
    </Container>
  );
};




